import React, { useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import DropCard from "./DropCard";
import { messages } from "../constants/messages";
import { CustomPalette } from "../constants/customPalette";
import LandingDropZone from "../Landing/LandingDropZone";
import usePrimaryColor from "../hooks/usePrimaryColor";
import { lightenColor } from "../utils/colorUtils";
import { MAX_FILE_SIZE } from "../constants/constants";
import { toMegabytes } from "../utils/helpers";

export default function Drop({
  setFile,
  setLoading,
  loading,
  dropDisabled,
  dropMessage,
  setDropMessage,
  version = 0,
  description,
  tipDescription,
  interfaceType = 0,
  noteDescription,
  fullWidthCard = false
}) {
  const { t } = useTranslation();
  const primaryColor = usePrimaryColor();

  const acceptFormat = useMemo(() => {
    if (version === 0) {
      return {
        "application/vnd.ms-excel": [".csv", ".xls", ".xlsx"],
        "application/zip": [".zip"],
        "application/x-zip-compressed": [".zip"],
        "application/json": [".json"]
      };
    }
    if (version === 1) {
      return {
        "application/zip": [".zip"],
        "application/x-zip-compressed": [".zip"],
        "application/json": [".json"],
        "text/yaml": [".yaml", ".yml"],
        "application/x-yaml": [".yaml", ".yml"]
      };
    }
    if (version === 2) {
      return {
        "application/vnd.ms-excel": [".csv", ".xls", ".xlsx"],
        "text/tab-separated-values": [".tsv"],
        "text/plain": [".tsv"]
      };
    }
    if (version === 3) {
      return {
        "application/json": [".json"]
      };
    }
    if (version === 4) {
      return {
        "application/vnd.ms-excel": [".xls", ".xlsx"]
      };
    }
    if (version === 5) {
      return {
        "application/vnd.ms-excel": [".csv"],
        "application/zip": [".zip"],
        "application/x-zip-compressed": [".zip"],
        "application/json": [".json"]
      };
    }
    if (version === 6) {
      return {
        "text/plain": [".txt"]
      };
    }
  }, [version]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptFormat,
    maxSize: MAX_FILE_SIZE,
    onDropRejected: (file) => {
      if (file[0].errors[0].code === "file-too-large") {
        setDropMessage({
          message: t("FileSizeError", {
            maxFileSize: toMegabytes(MAX_FILE_SIZE)
          }),
          type: "error"
        });
      } else if (file[0].errors[0].code === "file-invalid-type") {
        setDropMessage({
          message: messages.wrongTypeUploadFail,
          type: "error"
        });
      } else {
        setDropMessage({ message: messages.fileRejectedFail, type: "error" });
      }
      setTimeout(() => {
        setDropMessage({ message: "", type: "" });
      }, [3500]);
    },
    onDropAccepted: (files) => {
      setLoading(true);
      setFile(files);
    },
    disabled: dropDisabled
  });
  const [hover, setHover] = useState(false);

  const spinningAnimation =
    "spin 1.5s linear infinite; @keyframes spin {from {transform: rotate(0deg);}to {transform: rotate(-360deg);}";

  const handleHover = () => {
    setHover(true);
  };
  const handleHoverLeave = () => {
    setHover(false);
  };

  const handleDragOver = () => {
    setHover(true);
  };

  const handleDragLeave = () => {
    setHover(false);
  };

  const downloadIconColor = useMemo(
    () =>
      dropDisabled === true
        ? CustomPalette.GREY_600
        : hover === true
          ? lightenColor(primaryColor, 10)
          : primaryColor,
    [dropDisabled, hover, primaryColor]
  );

  return (
    <>
      {interfaceType === 1 ? (
        <LandingDropZone
          dropMessage={dropMessage}
          loading={loading}
          dropDisabled={dropDisabled}
          spinningAnimation={spinningAnimation}
          downloadIconColor={downloadIconColor}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          hover={hover}
          handleHover={handleHover}
          handleHoverLeave={handleHoverLeave}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
        />
      ) : (
        <DropCard
          dropMessage={dropMessage}
          loading={loading}
          dropDisabled={dropDisabled}
          spinningAnimation={spinningAnimation}
          downloadIconColor={downloadIconColor}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          hover={hover}
          handleHover={handleHover}
          handleHoverLeave={handleHoverLeave}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          description={description}
          tipDescription={tipDescription}
          noteDescription={noteDescription}
          fullWidthCard={fullWidthCard}
        />
      )}
    </>
  );
}
